import uuid from 'uuid/v4';
import PagesResource from '@/store/resource/pages';

const pagesResource = new PagesResource();
let frameScheduled = false;

export default {
  // ============================================
  // API actions (CRUD for page records)
  // ============================================

  /**
   * Fetch all page records from API.
   * Stores records but does NOT load page content into memory.
   */
  async fetchRecords({ commit }) {
    commit('setLoading', true);
    try {
      const { ok, data } = await pagesResource.get();
      if (ok && data) {
        commit('setRecords', data);
      }
      return { ok, data };
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Fetch a single page record by ID.
   * Stores record but does NOT load page content into memory.
   */
  async fetchRecord({ commit }, { recordId }) {
    commit('setLoading', true);
    try {
      const { ok, data } = await pagesResource.get(recordId);
      if (ok && data) {
        commit('setRecord', data);
      }
      return { ok, data };
    } finally {
      commit('setLoading', false);
    }
  },

  /**
   * Load a record's page content into memory for rendering.
   * Call this after fetchRecord when you want to actually render the page.
   */
  loadRecordPage({ commit, state }, { recordId }) {
    const record = state.recordsById[recordId];
    if (!record || !record.page) return null;

    const pageData = record.page;
    // Backend stores as pageId, frontend serializes as id - handle both
    const pageId = pageData.pageId || pageData.id || uuid();

    // Create page entry with link back to record
    commit('createPage', {
      pageId,
      meta: pageData.meta || {},
      recordId,
    });

    // Rebuild nodes from page content
    Object.values(pageData.nodes || {}).forEach((node) => {
      commit('insertNode', {
        node: { ...node, pageId },
      });
    });

    commit('setActivePage', pageId);
    return pageId;
  },

  /**
   * Save a page to the API.
   * Creates a new record if no recordId exists, otherwise updates.
   */
  async savePage(
    { commit, state, getters },
    { pageId, name = 'Untitled', pid = null }
  ) {
    const page = state.pagesById[pageId];
    if (!page) return { ok: false, error: 'Page not found' };

    commit('setSaving', true);
    try {
      // Serialize page content
      const serialized = getters.serializePage(pageId);
      if (!serialized) return { ok: false, error: 'Failed to serialize page' };

      const payload = {
        name,
        pid,
        page: serialized,
      };

      let result;
      if (page.recordId) {
        // Update existing record
        payload.id = page.recordId;
        result = await pagesResource.update(payload);
      } else {
        // Create new record
        result = await pagesResource.create(payload);
      }

      if (result.ok && result.data) {
        commit('setRecord', result.data);
        // Link page to record if newly created
        if (!page.recordId && result.data.id) {
          commit('setPageRecordId', { pageId, recordId: result.data.id });
        }
      }

      return result;
    } finally {
      commit('setSaving', false);
    }
  },

  /**
   * Delete a page record from the API.
   * Also removes local page content from memory.
   */
  async deleteRecord({ commit, state, dispatch }, { recordId }) {
    const record = state.recordsById[recordId];
    if (!record) return { ok: false, error: 'Record not found' };

    commit('setLoading', true);
    try {
      const result = await pagesResource.delete(recordId);

      if (result.ok) {
        // Find and delete local page if loaded
        const pageEntry = Object.values(state.pagesById).find(
          (p) => p.recordId === recordId
        );
        if (pageEntry) {
          dispatch('deletePage', { pageId: pageEntry.id });
        }
        commit('removeRecord', { recordId });
      }

      return result;
    } finally {
      commit('setLoading', false);
    }
  },

  // ============================================
  // Local page actions (in-memory only)
  // ============================================

  /**
   * Create a new empty page in memory (not saved to API).
   * Use savePage() to persist to backend.
   */
  createPage({ commit }, { meta = {} } = {}) {
    const pageId = uuid();
    commit('createPage', { pageId, meta, recordId: null });
    commit('setActivePage', pageId);
    return pageId;
  },

  /**
   * Delete a page from memory.
   * Does NOT delete from API - use deleteRecord for that.
   */
  deletePage({ commit, state }, { pageId }) {
    // Recursively remove all nodes for this page
    const nodeIds = Object.keys(state.nodesById).filter(
      (id) => state.nodesById[id].pageId === pageId
    );
    nodeIds.forEach((nodeId) => commit('removeNode', { nodeId }));

    commit('deletePage', { pageId });
  },

  /**
   * Load page content from a JSON object (for demos, imports, etc.).
   * Creates a local-only page - use savePage() to persist.
   */
  loadPage({ commit }, { json }) {
    const pageId = json.pageId || json.id || uuid();
    commit('createPage', { pageId, meta: json.meta || {}, recordId: null });

    // Rebuild nodes
    Object.values(json.nodes || {}).forEach((node) => {
      commit('insertNode', {
        node: { ...node, pageId },
      });
    });

    commit('setActivePage', pageId);
    return pageId;
  },

  // Single update - queued for batching
  applyUpdate({ commit, dispatch }, update) {
    commit('queueUpdate', update);
    dispatch('scheduleFlush');
  },

  // Batch updates
  applyUpdatesBatch({ commit, dispatch }, updates) {
    updates.forEach((update) => commit('queueUpdate', update));
    dispatch('scheduleFlush');
  },

  // Animation frame batching (per PRD section 6.2)
  scheduleFlush({ state, commit }) {
    if (frameScheduled) return;
    frameScheduled = true;

    requestAnimationFrame(() => {
      const updates = [...state.updateQueue];
      commit('clearUpdateQueue');
      frameScheduled = false;

      // Coalesce updates to same node
      const byNode = {};
      updates.forEach((u) => {
        if (!byNode[u.id]) byNode[u.id] = [];
        byNode[u.id].push(u);
      });

      // Apply coalesced updates
      Object.entries(byNode).forEach(([nodeId, nodeUpdates]) => {
        nodeUpdates.forEach((update) => {
          if (update.op === 'insert') {
            commit('insertNode', { node: update });
          } else if (update.op === 'patch' || update.props) {
            commit('patchNodeProps', { nodeId, props: update.props });
          } else if (update.op === 'appendChunk') {
            commit('appendChunk', { nodeId, text: update.text });
          } else if (update.op === 'move') {
            commit('moveNode', {
              nodeId,
              newParentId: update.newParentId,
              afterId: update.afterId,
            });
          } else if (update.op === 'remove') {
            commit('removeNode', { nodeId });
          }
        });
      });
    });
  },

  // Component registration
  registerComponent({ commit }, { nodeType, component }) {
    commit('registerComponent', { nodeType, component });
  },
};
